import React, { useContext } from 'react';
import { Typography, Flex, COLORS, Grid } from '@dripcapital/dripui-v1';
import { Formik, Field } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  TextFieldForFormik,
  BorderCardContainer,
  Spacer,
  AtomicButton,
  SelectForFormik,
} from 'atoms';
import {
  DAJ_COLORS,
  getSortedCountries,
  sanitizePhoneNumberWithCountryCode,
  scfOwnerSchema,
} from 'utils';
import { ButtonTypeEnum } from 'enums';
import Check from '@mui/icons-material/Check';
import Percent from '@mui/icons-material/Percent';
import CountryLabel from 'components/CountryLabel';
import { ScfOwnerType } from 'types';
import { US_OWNER_INFO_INTIAL_VALUES } from 'conversion';
import { AnalyticsContext } from 'shared/context/AnalyticsContext';

type Props = {
  indexNo?: number;
  loading: boolean;
  onCancel: React.MouseEventHandler<HTMLButtonElement>;
  onSave: (formValues: ScfOwnerType) => void;
  data?: ScfOwnerType;
};

const CompanyInfoForm: React.FC<Props> = ({
  indexNo,
  loading,
  onCancel,
  onSave,
  data,
}) => {
  const { t } = useTranslation();

  const { mixpanel } = useContext(AnalyticsContext);

  return (
    <Formik
      initialValues={data ? data : US_OWNER_INFO_INTIAL_VALUES}
      onSubmit={(formValues) =>
        onSave({
          ...formValues,
          phone: {
            countryCode: formValues.phone?.countryCode || '',
            phoneNumber: sanitizePhoneNumberWithCountryCode(
              formValues.phone?.phoneNumber,
              formValues.phone?.countryCode
            ),
          },
        })
      }
      validationSchema={scfOwnerSchema}
    >
      {(formik) => {
        const { handleSubmit, isValid, isSubmitting, errors } = formik;

        if (!isValid && isSubmitting) {
          mixpanel.track('company-shareholders-form-error-triggered', {
            error: errors,
          });
          const names = Object.keys(errors);
          if (names.length > 0)
            document
              .querySelector(`input[name='${names[0]}']`)
              ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return (
          <BorderCardContainer>
            <Grid container columnSpacing={3} rowSpacing={2}>
              <Grid xs={12} item>
                <Typography variant="h4" fontWeight="bold" color="secondary">
                  {t('usoa_cd_owner_details', {
                    index: indexNo,
                  })}
                </Typography>
              </Grid>
              <Grid xs={6} item>
                <Field
                  name="firstname"
                  label={t('usoa_cd_first_name')}
                  placeholder={t('usoa_cd_first_name_placeholder')}
                  component={TextFieldForFormik}
                  fullWidth
                />
              </Grid>
              <Grid xs={6} item>
                <Field
                  name="lastname"
                  label={t('usoa_cd_last_name')}
                  placeholder={t('usoa_cd_last_name_placeholder')}
                  component={TextFieldForFormik}
                  fullWidth
                />
              </Grid>
              <Grid xs={6} item>
                <Typography
                  variant="label"
                  _sx={{ marginBottom: 1 }}
                  color="secondary"
                >
                  {t('label_phone_number')}
                </Typography>
                <Grid container columnSpacing={2}>
                  <Grid xs={12} item>
                    <Flex direction={'row'} flexWrap={'nowrap'}>
                      <Flex>
                        <Field
                          name="phone.countryCode"
                          options={getSortedCountries()?.map(
                            (countryAndCallingCode) => ({
                              label: (
                                <CountryLabel
                                  countryAndCallingCode={countryAndCallingCode}
                                />
                              ),
                              value: `${countryAndCallingCode.countryCode}-${countryAndCallingCode.callingCode}`,
                            })
                          )}
                          placeholder={t('placeholder_phone_cc')}
                          translate={false}
                          component={SelectForFormik}
                          _sx={{
                            height: 56,
                            maxWidth: '116px',
                            borderRadius: '8px 0 0 8px',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderRightWidth: 0,
                              borderColor: DAJ_COLORS.NOBLE_BLUE_500,
                            },
                          }}
                          fullWidth
                        />
                      </Flex>
                      <Flex flexGrow={1}>
                        <Field
                          name="phone.phoneNumber"
                          placeholder={t('placeholder_phone_number')}
                          component={TextFieldForFormik}
                          _sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderRadius: '0 8px 8px 0',
                              borderLeftWidth: 0,
                            },
                          }}
                          fullWidth
                        />
                      </Flex>
                    </Flex>
                  </Grid>
                </Grid>
              </Grid>
              <Grid xs={6} item>
                <Field
                  name="ownershipPercentage"
                  label={t('usoa_cd_form_ownership_percentage')}
                  placeholder="Enter percentage (e.g. 30)"
                  type="number"
                  endIcon={<Percent sx={{ color: DAJ_COLORS.BLUE }} />}
                  component={TextFieldForFormik}
                  fullWidth
                />
              </Grid>
              <Grid xs={6} item>
                <Field
                  name="email"
                  type="email"
                  label={t('usoa_cd_email_placeholder')}
                  placeholder="Enter email"
                  component={TextFieldForFormik}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Spacer verticalSpace={3} />
            <Flex spacing={3}>
              <AtomicButton
                dataTestId="owner-save-form-cta"
                loading={loading}
                onClick={() => {
                  handleSubmit();
                }}
                _sx={{
                  paddingX: 8,
                }}
                variant={ButtonTypeEnum.CONTAINED}
                endIcon={<Check />}
              >
                {data?.id ? t('btn_save') : t('btn_add')}
              </AtomicButton>
              <AtomicButton
                dataTestId="owner-cancel-form-cta"
                onClick={onCancel}
                variant={ButtonTypeEnum.TEXT}
                _sx={{ color: COLORS.PRIMARY, paddingX: 4 }}
              >
                {t('btn_cancel')}
              </AtomicButton>
            </Flex>
            <Spacer verticalSpace={3} />
          </BorderCardContainer>
        );
      }}
    </Formik>
  );
};

export default CompanyInfoForm;
